import { ATTACK_RANGE_FLASH_EXPAND_MS } from "#/game/animation/constants";

export type AttackRangeFlashOrigin = { x: number; y: number };

type FlashPhase = "hidden" | "expand" | "hold";

type FlashAnimState = {
  phase: FlashPhase;
  phaseStartedAtMs: number;
  origin: AttackRangeFlashOrigin;
  tileKeys: string[];
};

const INITIAL_ANIM: FlashAnimState = {
  phase: "hidden",
  phaseStartedAtMs: 0,
  origin: { x: 0, y: 0 },
  tileKeys: [],
};

let anim: FlashAnimState = { ...INITIAL_ANIM, origin: { x: 0, y: 0 }, tileKeys: [] };

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function sameOrigin(a: AttackRangeFlashOrigin, b: AttackRangeFlashOrigin): boolean {
  return a.x === b.x && a.y === b.y;
}

function sameTileKeys(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((key) => set.has(key));
}

function clearFlash(): void {
  anim = {
    ...INITIAL_ANIM,
    origin: { x: 0, y: 0 },
    tileKeys: [],
  };
}

function startExpand({
  origin,
  tileKeys,
  nowMs,
}: {
  origin: AttackRangeFlashOrigin;
  tileKeys: string[];
  nowMs: number;
}): void {
  anim = {
    phase: "expand",
    phaseStartedAtMs: nowMs,
    origin: { ...origin },
    tileKeys: [...tileKeys],
  };
}

function advancePhase(nowMs: number): void {
  if (anim.phase !== "expand") return;
  if (nowMs - anim.phaseStartedAtMs < ATTACK_RANGE_FLASH_EXPAND_MS) return;
  anim = { ...anim, phase: "hold", phaseStartedAtMs: nowMs };
}

function desiredMatchesCurrent({
  origin,
  tileKeys,
}: {
  origin: AttackRangeFlashOrigin;
  tileKeys: readonly string[];
}): boolean {
  return sameOrigin(anim.origin, origin) && sameTileKeys(anim.tileKeys, tileKeys);
}

export function resetAttackRangeFlashAnimationForTests(): void {
  clearFlash();
}

export function isAttackRangeFlashAnimating(): boolean {
  return anim.phase !== "hidden";
}

export function attackRangeFlashTravel(nowMs: number): number {
  if (anim.phase === "hidden") return 0;
  if (anim.phase === "hold") return 1;
  const raw = clamp01((nowMs - anim.phaseStartedAtMs) / Math.max(1, ATTACK_RANGE_FLASH_EXPAND_MS));
  return easeOutCubic(raw);
}

export function syncAttackRangeFlashAnimation({
  origin,
  tileKeys,
  nowMs,
  readyToExpand,
}: {
  origin: AttackRangeFlashOrigin | null;
  tileKeys: readonly string[];
  nowMs: number;
  readyToExpand: boolean;
}): void {
  const desiredKeys = [...tileKeys];
  const desiredEmpty = !origin || desiredKeys.length === 0;

  if (desiredEmpty || !readyToExpand) {
    clearFlash();
    return;
  }

  if (anim.phase === "hidden" || !desiredMatchesCurrent({ origin, tileKeys: desiredKeys })) {
    startExpand({ origin, tileKeys: desiredKeys, nowMs });
    return;
  }

  advancePhase(nowMs);
}

export function getAttackRangeFlashDrawState(nowMs: number): {
  origin: AttackRangeFlashOrigin;
  tileKeys: readonly string[];
  travel: number;
  pulseStartedAtMs: number | null;
} | null {
  if (anim.phase === "hidden") return null;
  return {
    origin: anim.origin,
    tileKeys: anim.tileKeys,
    travel: attackRangeFlashTravel(nowMs),
    pulseStartedAtMs: anim.phase === "hold" ? anim.phaseStartedAtMs : null,
  };
}
